import React from 'react';
import clsx from 'clsx';
import {Icon, type IconDefinition} from '@netx/core-icons';
import {
	ListItemIcon,
	ListItemText,
	type MenuItemTypeMap,
	MenuItem as MuiMenuItem,
	type MenuItemProps as MuiMenuItemProps,
} from '@mui/material';
import {alpha} from '@mui/system';
import {unstable_capitalize} from '@mui/utils';
import {ColoredAvatar} from '@netx/core-components/ColoredAvatar';
import {
	composeClasses,
	styled,
	type WithClasses,
	type WithOwnerState,
} from '@netx/core-components/Theme';
import {
	getMenuItemUtilityClass,
	MENU_ITEM_CLASS_NAME,
	menuItemClasses,
	MenuItemClasses,
} from './menuItemClasses';

type MenuItemOwnerState = WithClasses<
	MenuItemClasses,
	{
		active?: boolean;
		depth?: number;
		dense?: boolean;
		proxy?: boolean;
		severity: 'default' | 'error' | 'warning';
	}
>;

const useUtilityClasses = (ownerState: MenuItemOwnerState) => {
	const {classes, active, severity} = ownerState;
	const slots = {
		root: [
			'root',
			active && 'active',
			severity &&
				severity !== 'default' &&
				`severity${unstable_capitalize(severity)}`,
		],
		coloredAvatar: ['coloredAvatar'],
		icon: ['icon'],
	};
	return composeClasses(slots, getMenuItemUtilityClass, classes);
};

const MenuItemRoot = styled(MuiMenuItem, {
	name: MENU_ITEM_CLASS_NAME,
	slot: 'Root',
})(({theme, ownerState}: WithOwnerState<MenuItemOwnerState>) => {
	const {severity, dense, depth, proxy} = ownerState || {};
	return {
		minHeight: 36,
		...(!severity || severity === 'default'
			? {
					[`&.${menuItemClasses.active}`]: {
						backgroundColor: theme.palette.action.hover,
					},
				}
			: {
					background: theme.palette[severity].main,
					[`&, & .${menuItemClasses.icon}`]: {
						color: theme.palette[severity].contrastText,
					},
					[`& .MuiListItemText-secondary`]: {
						color: alpha(theme.palette[severity].contrastText, 0.68),
					},
					[`&.${menuItemClasses.active}, &:hover, &:active, &:focus`]: {
						background: theme.palette[severity].dark,
					},
				}),
		...(depth
			? {
					paddingLeft: theme.spacing(depth * 6),
					backgroundColor: theme.palette.custom.background.accent,
				}
			: null),
		...(proxy
			? {
					pointerEvents: 'none',
				}
			: null),
		...(dense && {
			[`& .${menuItemClasses.icon}`]: {
				'& svg': {
					fontSize: 16,
				},
			},
		}),
		// larger touch area for mobile
		[theme.breakpoints.down('sm')]: {
			minHeight: 48,
		},
	};
});

const MenuItemIcon = styled(ListItemIcon, {
	name: MENU_ITEM_CLASS_NAME,
	slot: 'Icon',
})(({theme, ownerState}) => ({
	color: theme.palette.text.primary,
	justifyContent: 'center',
	marginRight: 8,
	marginLeft: -4,
	// it is just too darn wide
	minWidth: 32,
	'& svg': {
		fontSize: 16,
	},
}));

const MenuItemColorAvatar = styled(ColoredAvatar, {
	name: MENU_ITEM_CLASS_NAME,
	slot: 'ColoredAvatar',
})(({theme}) => ({
	height: 20,
	width: 20,
	fontSize: 12,
	marginRight: theme.spacing(2),
}));

export type MenuItemProps<
	RootComponent extends React.ElementType = MenuItemTypeMap['defaultComponent'],
> = WithClasses<MenuItemClasses, Partial<MuiMenuItemProps<RootComponent>>> & {
	active?: boolean;
	/**
	 * If true, the menu item will display a colored avatar.
	 *
	 * The color and value of the avatar will be determined by the first letter of the `text` prop.
	 *
	 * NOTE: If `icon` is also provided, it will be displayed instead of the avatar.
	 */
	colorAvatar?: boolean;
	depth?: number;
	endIcon?: IconDefinition;
	icon?: IconDefinition;
	proxy?: boolean;
	secondaryText?: string;
	severity?: 'default' | 'error' | 'warning';
	text: string;
};

export const MenuItemPrimitive = <
	RootComponent extends React.ElementType = MenuItemTypeMap['defaultComponent'],
>(
	{
		classes: _classes,
		className,
		active,
		colorAvatar,
		depth,
		endIcon,
		icon,
		proxy,
		secondaryText,
		severity = 'default',
		text,
		...props
	}: MenuItemProps<RootComponent>,
	ref: React.ForwardedRef<HTMLLIElement>,
) => {
	const ownerState: MenuItemOwnerState = {
		classes: _classes,
		active,
		depth,
		proxy,
		severity,
		dense: props.dense,
	};

	const classes = useUtilityClasses(ownerState);

	return (
		<MenuItemRoot
			aria-label={text}
			className={clsx(classes.root, className)}
			ref={ref}
			role={undefined}
			{...props}
			ownerState={ownerState}
		>
			{icon ? (
				<MenuItemIcon
					className={classes.icon}
					ownerState={{dense: props.dense}}
				>
					<Icon icon={icon} />
				</MenuItemIcon>
			) : colorAvatar ? (
				<MenuItemColorAvatar label={text} />
			) : null}
			<ListItemText
				primary={text}
				secondary={secondaryText}
				primaryTypographyProps={{noWrap: true}}
			/>
			{endIcon && (
				<div style={{marginLeft: 16}}>
					<Icon icon={endIcon} />
				</div>
			)}
		</MenuItemRoot>
	);
};

export const MenuItem = React.forwardRef(
	MenuItemPrimitive,
) as typeof MenuItemPrimitive;

export default MenuItem;
